import { Injectable } from '@angular/core';
import { AlertView, Mute } from '@netfoundry-ui/shared/model';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subject } from 'rxjs';
import { SideDrawerCoordinatorService } from './side-drawer-coordinator.service';

/** This drawer's id with the side-drawer coordinator (only one shell drawer open at a time). */
const DRAWER_ID = 'alert-detail';

/** Position of the open alert within its sibling list, for the header pager. */
export interface AlertDrawerNavState {
    index: number; // -1 when the open alert isn't in the list
    total: number;
    hasPrev: boolean;
    hasNext: boolean;
}

/**
 * Shared state for the in-layout alert-detail drawer. The host lives in the cloud-ziti app
 * shell (a flex sibling of <main>) and any feature — e.g. the dashboard's Recent Alerts pod —
 * can open it via this service. Mirrors DialLogsTimelineDrawerService; width persists across opens.
 */
@Injectable({ providedIn: 'root' })
export class AlertDetailDrawerService {
    // Width bounds are the shared drawer's — every shell drawer uses one width (see the coordinator).
    static readonly MIN_WIDTH = SideDrawerCoordinatorService.MIN_WIDTH;
    static readonly MAX_WIDTH = SideDrawerCoordinatorService.MAX_WIDTH;

    private readonly _open = new BehaviorSubject<boolean>(false);
    private readonly _alert = new BehaviorSubject<AlertView | null>(null);
    private readonly _mute = new BehaviorSubject<Mute | undefined>(undefined);
    private readonly _siblings = new BehaviorSubject<AlertView[]>([]);
    private readonly _refreshed = new Subject<AlertView>();
    private readonly _openInAlerts = new BehaviorSubject<boolean>(true);

    readonly open$ = this._open.asObservable();
    readonly alert$ = this._alert.asObservable();
    readonly mute$ = this._mute.asObservable();
    /** The alert re-read after a triage action or a reload, so the views that count alerts can read again. */
    readonly refreshed$ = this._refreshed.asObservable();
    /** Whether the footer links to the network Alerts page. Off where that page reads another network, as in frontdoor. */
    readonly openInAlerts$ = this._openInAlerts.asObservable();
    /** Shared across all shell drawers so switching type keeps the panel width. */
    readonly width$: Observable<number>;

    /** Where the open alert sits in its sibling list — drives the header pager. */
    readonly nav$: Observable<AlertDrawerNavState> = combineLatest([this._alert, this._siblings]).pipe(
        map(([alert, siblings]) => {
            const index = alert ? siblings.findIndex((s) => s.id === alert.id) : -1;
            return {
                index,
                total: siblings.length,
                hasPrev: index > 0,
                hasNext: index >= 0 && index < siblings.length - 1,
            };
        })
    );

    constructor(private coordinator: SideDrawerCoordinatorService) {
        this.width$ = coordinator.width$;
        // Another shell drawer opened — stand down so only one is ever on screen.
        this.coordinator.opened$.pipe(filter((id) => id !== DRAWER_ID)).subscribe(() => this.close());
        // Leaving the page it was opened on — dismiss it so it never lingers onto the next route.
        this.coordinator.closeAll$.subscribe(() => this.close());
    }

    get isOpen(): boolean {
        return this._open.value;
    }

    get currentAlertId(): string {
        return this._alert.value?.id || '';
    }

    /**
     * Open the drawer for one alert. `siblings` is the ordered list to page through (e.g. the
     * pod's rows); `mute` is the covering mute the caller already holds, for the Muting tab.
     * `openInAlerts: false` hides the link to the network Alerts page.
     */
    open(alert: AlertView, opts: { mute?: Mute; siblings?: AlertView[]; openInAlerts?: boolean } = {}): void {
        this.coordinator.notifyOpened(DRAWER_ID);
        this._openInAlerts.next(opts.openInAlerts ?? true);
        this._siblings.next(opts.siblings ?? []);
        this._alert.next(alert);
        this._mute.next(opts.mute);
        this._open.next(true);
    }

    /** Replace the alert on screen (e.g. after a re-read following an action). */
    setAlert(alert: AlertView): void {
        this._alert.next(alert);
        this._refreshed.next(alert);
    }

    /**
     * Step to the previous (-1) or next (1) alert in the sibling list. The precise covering mute
     * is not carried for siblings — the drawer falls back to the mute id — so it is cleared here.
     */
    navigate(direction: -1 | 1): void {
        const list = this._siblings.value;
        const current = this._alert.value;
        const index = current ? list.findIndex((s) => s.id === current.id) : -1;
        const target = list[index + direction];
        if (index < 0 || !target) {
            return;
        }
        this._alert.next(target);
        this._mute.next(undefined);
    }

    close(): void {
        this._open.next(false);
    }

    setWidth(px: number): void {
        this.coordinator.setWidth(px);
    }
}
